import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ExternalLink,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import {
  getPhonePlaceholder,
  sanitizePhoneInput,
} from '../../utils/phone';
import {
  compose,
  email as emailRule,
  errorsFromApi,
  formatError,
  maxLength,
  minLength,
  phone as phoneRule,
  required,
  validateForm,
} from '../../utils/formValidation';

const SCHEMA = {
  full_name: compose(required, minLength(2), maxLength(200)),
  email: compose(required, emailRule),
  phone: compose(required, phoneRule),
  message: compose(required, minLength(10), maxLength(5000)),
  subject: maxLength(200),
};

const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// Always render a real Google Maps embed URL. Never feed a regular
// `maps.google.com/?q=...` link as iframe `src` — Google blocks it via
// X-Frame-Options. Use the explicit `google_maps_embed_url` field if set,
// otherwise build a search-by-address embed URL.
const buildMapEmbed = (clinic) => {
  if (clinic?.google_maps_embed_url) return clinic.google_maps_embed_url;
  const q = encodeURIComponent(
    clinic?.clinic_address || `${clinic?.clinic_name || ''} Alanya`
  );
  return `https://www.google.com/maps?q=${q}&output=embed`;
};

const buildMapLink = (clinic) => {
  if (clinic?.google_maps_url) return clinic.google_maps_url;
  const q = encodeURIComponent(
    clinic?.clinic_address || `${clinic?.clinic_name || ''} Alanya`
  );
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

const sanitizeTel = (v) => String(v || '').replace(/[^\d+]/g, '');
const sanitizeWa = (v) => String(v || '').replace(/[^\d]/g, '');

const initialForm = (lang) => ({
  full_name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  preferred_language: lang,
  service_interest: '',
});

export default function ContactSection({ clinic }) {
  const { t, currentLanguage } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => initialForm(currentLanguage));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [banner, setBanner] = useState(null); // { type, msg }
  const [done, setDone] = useState(false);

  useEffect(() => {
    setForm((prev) => ({ ...prev, preferred_language: currentLanguage }));
  }, [currentLanguage]);

  const tErr = (key) => formatError(key, t);

  const runField = (name, value) => {
    const validator = SCHEMA[name];
    if (!validator) return;
    const err = validator(value);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) runField(name, value);
  };

  const handlePhoneChange = (e) => {
    const value = sanitizePhoneInput(e.target.value);
    setForm((prev) => ({ ...prev, phone: value }));
    if (touched.phone) runField('phone', value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    runField(name, value);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner(null);

    const found = validateForm(form, SCHEMA);
    if (Object.keys(found).length) {
      setErrors(found);
      setTouched(
        Object.keys(SCHEMA).reduce((acc, k) => ({ ...acc, [k]: true }), {})
      );
      setBanner({
        type: 'error',
        msg: t('validation.fixErrors', 'Please fix the highlighted fields and try again.'),
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          preferred_language: currentLanguage,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Surface server-side field errors directly on the form when present.
        const apiErrs = errorsFromApi(data);
        if (apiErrs) {
          setErrors(apiErrs);
          setTouched(
            Object.keys(apiErrs).reduce((acc, k) => ({ ...acc, [k]: true }), {})
          );
          setBanner({
            type: 'error',
            msg: t('validation.fixErrors', 'Please fix the highlighted fields and try again.'),
          });
        } else if (res.status === 429) {
          setBanner({
            type: 'error',
            msg: t('validation.rateLimit', 'Too many requests — please try again later.'),
          });
        } else {
          setBanner({
            type: 'error',
            msg:
              data?.message ||
              t('validation.serverError', 'Something went wrong on our side. Please try again.'),
          });
        }
        return;
      }

      toast.success(
        t('contact.form.success', "Thank you! We'll contact you shortly.")
      );
      setForm(initialForm(currentLanguage));
      setErrors({});
      setTouched({});
      setBanner(null);
      setDone(true);
    } catch (err) {
      setBanner({
        type: 'error',
        msg: t('validation.serverError', 'Something went wrong on our side. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const phoneNumber = useMemo(() => sanitizeTel(clinic?.clinic_phone), [clinic]);
  const waNumber = useMemo(() => sanitizeWa(clinic?.clinic_whatsapp), [clinic]);
  const mapEmbed = useMemo(() => buildMapEmbed(clinic), [clinic]);
  const mapLink = useMemo(() => buildMapLink(clinic), [clinic]);
  const phonePlaceholder = useMemo(
    () => getPhonePlaceholder(currentLanguage),
    [currentLanguage]
  );

  return (
    <section id="contact" className="section-padding section-tone-d home-contact">
      <div className="container-premium">
        <div className="section-head">
          <span className="section-eyebrow">
            {t('contact.eyebrow', 'Get in touch')}
          </span>
          <h2 className="section-title">
            {t('contact.title', 'Ready for Your New Smile?')}
          </h2>
          <p className="section-sub">
            {t(
              'contact.subtitle',
              'Send a message — our team will reply within a few minutes during opening hours.'
            )}
          </p>
        </div>

        <div className="home-contact-grid">
          {/* LEFT: Info + Map */}
          <aside className="home-contact-info">
            <ul className="home-contact-list">
              {clinic?.clinic_address && (
                <li>
                  <span className="home-contact-icon">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <strong>
                      {t('contact.info.addressLabel', 'Address')}
                    </strong>
                    <span>{clinic.clinic_address}</span>
                  </div>
                </li>
              )}
              {clinic?.clinic_phone && (
                <li>
                  <span className="home-contact-icon">
                    <Phone size={18} />
                  </span>
                  <div>
                    <strong>{t('contact.info.phoneLabel', 'Phone')}</strong>
                    <a href={`tel:${phoneNumber}`}>{clinic.clinic_phone}</a>
                  </div>
                </li>
              )}
              {clinic?.clinic_whatsapp && (
                <li>
                  <span className="home-contact-icon">
                    <MessageCircle size={18} />
                  </span>
                  <div>
                    <strong>
                      {t('contact.info.whatsappLabel', 'WhatsApp')}
                    </strong>
                    <a
                      href={`https://wa.me/${waNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {clinic.clinic_whatsapp}
                    </a>
                  </div>
                </li>
              )}
              <li>
                <span className="home-contact-icon">
                  <Clock size={18} />
                </span>
                <div>
                  <strong>
                    {t('contact.info.hoursLabel', 'Working hours')}
                  </strong>
                  <span>
                    {t('contact.info.hoursValue', 'Mon–Sat · 09:00 – 19:00')}
                  </span>
                </div>
              </li>
            </ul>

            <div className="home-contact-map">
              <iframe
                title="Crystal Dental Location"
                src={mapEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <a
                href={mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="home-contact-map-cta"
              >
                {t('contactPage.map.open', 'Open in Google Maps')}
                <ExternalLink size={13} />
              </a>
            </div>
          </aside>

          {/* RIGHT: Form (or success card after submit) */}
          {done ? (
            <div className="home-contact-form home-contact-success">
              <div className="appointment-success">
                <div className="appointment-success-icon">
                  <CheckCircle2 size={32} />
                </div>
                <h3>
                  {t(
                    'contact.form.success',
                    "Thank you! We'll contact you shortly."
                  )}
                </h3>
                <p style={{ color: 'rgba(11,79,108,0.72)', maxWidth: 420 }}>
                  {t(
                    'contact.form.successDetail',
                    'Your message is on its way to our coordinators. Most replies arrive within a few minutes during opening hours.'
                  )}
                </p>
                <div className="appointment-success-cta">
                  {waNumber ? (
                    <a
                      href={`https://wa.me/${waNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-crystal"
                    >
                      <MessageCircle size={16} />
                      {t('appointmentPage.side.whatsapp', 'WhatsApp us')}
                    </a>
                  ) : null}
                  <button
                    type="button"
                    className="btn-crystal-outline"
                    onClick={() => setDone(false)}
                  >
                    {t('contact.form.sendAnother', 'Send another message')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
          <form className="home-contact-form" onSubmit={submit} noValidate>
            <header className="home-contact-form-head">
              <h3>{t('contactPage.form.title', 'Send us a message')}</h3>
              <p>
                {t(
                  'contactPage.form.subtitle',
                  "Tell us a bit about what you're looking for — we'll come back with a personalised plan."
                )}
              </p>
            </header>

            {banner && (
              <div className={`form-banner ${banner.type}`} role="alert">
                <AlertCircle size={18} />
                <span>{banner.msg}</span>
              </div>
            )}

            <div className="home-contact-fields">
              <div className="home-contact-row two">
                <div className={`field ${errors.full_name ? 'has-error' : ''}`}>
                  <label htmlFor="hc-full_name" className="field-label">
                    {t('contact.form.name', 'Full name')} <span className="req">*</span>
                  </label>
                  <input
                    id="hc-full_name"
                    name="full_name"
                    className="field-input"
                    value={form.full_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errors.full_name)}
                    aria-describedby={errors.full_name ? 'hc-full_name-err' : undefined}
                    placeholder={t('contact.form.name', 'Full name')}
                  />
                  {errors.full_name && (
                    <span id="hc-full_name-err" className="field-error">
                      <AlertCircle size={13} /> {tErr(errors.full_name)}
                    </span>
                  )}
                </div>
                <div className={`field ${errors.phone ? 'has-error' : ''}`}>
                  <label htmlFor="hc-phone" className="field-label">
                    {t('contact.form.phone', 'Phone number')} <span className="req">*</span>
                  </label>
                  <input
                    id="hc-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className="field-input"
                    value={form.phone}
                    onChange={handlePhoneChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? 'hc-phone-err' : undefined}
                    placeholder={phonePlaceholder}
                  />
                  {errors.phone && (
                    <span id="hc-phone-err" className="field-error">
                      <AlertCircle size={13} /> {tErr(errors.phone)}
                    </span>
                  )}
                </div>
              </div>
              <div className="home-contact-row two">
                <div className={`field ${errors.email ? 'has-error' : ''}`}>
                  <label htmlFor="hc-email" className="field-label">
                    {t('contact.form.email', 'Email address')} <span className="req">*</span>
                  </label>
                  <input
                    id="hc-email"
                    name="email"
                    type="email"
                    className="field-input"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'hc-email-err' : undefined}
                    placeholder="you@example.com"
                  />
                  {errors.email && (
                    <span id="hc-email-err" className="field-error">
                      <AlertCircle size={13} /> {tErr(errors.email)}
                    </span>
                  )}
                </div>
                <div className={`field ${errors.subject ? 'has-error' : ''}`}>
                  <label htmlFor="hc-subject" className="field-label">
                    {t('contact.form.subject', 'Treatment / Subject')}
                  </label>
                  <input
                    id="hc-subject"
                    name="subject"
                    className="field-input"
                    value={form.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errors.subject)}
                    aria-describedby={errors.subject ? 'hc-subject-err' : undefined}
                    placeholder={t('contact.form.subject', 'Treatment / Subject')}
                  />
                  {errors.subject && (
                    <span id="hc-subject-err" className="field-error">
                      <AlertCircle size={13} /> {tErr(errors.subject)}
                    </span>
                  )}
                </div>
              </div>
              <div className={`field ${errors.message ? 'has-error' : ''}`}>
                <label htmlFor="hc-message" className="field-label">
                  {t('contact.form.message', 'Your message')} <span className="req">*</span>
                </label>
                <textarea
                  id="hc-message"
                  name="message"
                  className="field-textarea"
                  value={form.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={5}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'hc-message-err' : undefined}
                  placeholder={t('contact.form.message', 'Your message')}
                />
                {errors.message && (
                  <span id="hc-message-err" className="field-error">
                    <AlertCircle size={13} /> {tErr(errors.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="home-contact-actions">
              <button
                type="submit"
                className="btn-crystal full"
                disabled={submitting}
              >
                {submitting ? (
                  t('contact.form.sending', 'Sending...')
                ) : (
                  <>
                    <Send size={16} />
                    {t('contact.form.submit', 'Send Message')}
                  </>
                )}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </section>
  );
}
